import { LogsEvent, LogsInitConfiguration } from '@datadog/browser-logs';
import { DefaultPrivacyLevel, RumEvent, RumInitConfiguration } from '@datadog/browser-rum';

const datadogClientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN || '';
const environmentName = process.env.REACT_APP_ENVIRONMENT_NAME || 'local';
const isLocal = environmentName === 'local';

const KNOWN_ERRORS = {
  FLASH: ['XHR error GET https://api.maptiler.com/', 'XHR error GET https://basemaps.arcgis.com/'],
};

const isFlashError = (event: LogsEvent) =>
  new URL(event.view.url).pathname.startsWith('/flash/') &&
  KNOWN_ERRORS.FLASH.some(err => event.message.startsWith(err));

const getProperServiceName = (servicePath: string) => {
  switch (servicePath) {
    case 'sales':
    case 'admin-settings':
    case 'user-settings':
    case 'external-links':
      return 'tc3-sales';
    case 'truck-appointments':
      return 'tc3-appointment';
    case 'workflow':
      return 'tc3-workflow';
    case 'client-inventories':
      return 'tc3-inventory';
    case 'invoices':
      return 'tc3-invoicing';
    case 'projects':
    case 'claims':
    case 'purchase-requests':
    case 'purchase-orders':
    case 'purchase-invoices':
      return 'tc3-projects';
    case 'flash':
      return 'tc3-flash';
    case 'operational-sites':
      return 'tc3-operational-sites';
    case 'telemetry':
      return 'tc3-telemetry';
    case 'vessels':
      return 'tc3-vessel';
    default:
      return servicePath;
  }
};

// MFE bundles are served at /tc3-<name>/entrypoint.min.js. Pulling the segment
// from a stack frame attributes async errors to the MFE that produced them
// rather than the page the user happens to be on.
const MFE_STACK_URL_RE = /\/(tc3-[\w-]+)\//;

const getServiceFromStack = (stack: string | undefined) => {
  if (!stack) return undefined;
  return stack.match(MFE_STACK_URL_RE)?.[1];
};

const getServiceFromUrl = () => {
  const { pathname } = new URL(window.location.href);
  const segment = pathname.split('/')[1];
  return segment ? getProperServiceName(segment) : undefined;
};

const applyAttribution = (
  event: { service?: string; version?: string },
  service: string | undefined,
) => {
  if (!service) return;
  // Override service+version atomically: a partial override (MFE service + portal version)
  // is a pair no sourcemap upload covers, so Datadog reports "Unminification failed".
  const mfeVersion = window.qsl?.tc3?.apps?.[service]?.version;
  if (!mfeVersion) return;
  event.service = service;
  event.version = mfeVersion;
};

export const datadogLogsConfiguration: LogsInitConfiguration = {
  clientToken: datadogClientToken,
  env: environmentName,
  service: 'tc3-portal',
  version: require('../package.json').version, // eslint-disable-line @typescript-eslint/no-var-requires
  // Forward CSP violation reports to Logs as well as RUM (TC3-17078), so they are searchable next to backend logs.
  forwardReports: ['csp_violation'],
  beforeSend: (event: LogsEvent) => {
    if (event.status !== 'error') return true;
    if (isFlashError(event)) return false;
    if (document.hidden) return false;

    if (!navigator.onLine || document.wasDiscarded) {
      event.additionalBrowserInfo = {
        navigatorOnLine: navigator.onLine,
        documentWasDiscarded: document.wasDiscarded,
      };
    }

    applyAttribution(event, getServiceFromStack(event.error?.stack) ?? getServiceFromUrl());
    return true;
  },
};

export const getDatadogLogsConfiguration = () => {
  return datadogClientToken && !isLocal ? datadogLogsConfiguration : null;
};

export const getDatadogRumConfiguration = (): RumInitConfiguration | null => {
  const allowedTracingUrls = isLocal
    ? []
    : [new RegExp(`^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/[\\w-]+/api/.*$`)];

  return datadogClientToken && !isLocal
    ? {
        applicationId: process.env.REACT_APP_DATADOG_RUM_APP_ID || '',
        clientToken: datadogClientToken,
        env: environmentName,
        service: 'tc3-portal',
        version: require('../package.json').version, // eslint-disable-line @typescript-eslint/no-var-requires
        defaultPrivacyLevel: DefaultPrivacyLevel.ALLOW,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        startSessionReplayRecordingManually: true,
        allowedTracingUrls,
        beforeSend: (event: RumEvent, context: unknown) => {
          // Datadog passes context.handlingStack for error events: the synchronous
          // stack at the point the SDK captured the error. This is the most
          // reliable signal for which MFE owns the failure.
          const handlingStack = (context as { handlingStack?: string } | undefined)?.handlingStack;
          const errorStack = event.type === 'error' ? event.error?.stack : undefined;
          const service =
            getServiceFromStack(handlingStack) ??
            getServiceFromStack(errorStack) ??
            getServiceFromUrl();
          applyAttribution(event, service);
          return true;
        },
      }
    : null;
};
